import { useCurrentWorkspace, useCustomQuery } from '@gripp/shared-logic';
import { rangeToDates } from './useHomeActivityMap';
import { TimeRange } from '../types';

/**
 * Shape of a single activity summary bucket.
 * Mirrors the generated `HomeActivityBucket` from backend codegen —
 * verified schema-first from backend-workspace/apps/gripp-api/generated/index.ts.
 * Bucket start is inclusive, end is exclusive; gap-filled with zero buckets.
 */
export interface HomeActivityBucket {
  start: string;
  end: string;
  urgent: number;
  pending: number;
  solved: number;
}

/**
 * Top-level activity summary for the Activity card.
 * Mirrors the generated `HomeActivitySummary`.
 */
export interface HomeActivitySummary {
  total: number;
  urgent: number;
  pending: number;
  solved: number;
  buckets: HomeActivityBucket[];
}

const HOME_ACTIVITY_SUMMARY_QUERY = `
  query HomeActivitySummary($filter: HomeActivityFilter) {
    homeActivitySummary(filter: $filter) {
      total
      urgent
      pending
      solved
      buckets {
        start
        end
        urgent
        pending
        solved
      }
    }
  }
`;

/**
 * Fetches time-bucketed activity counts for the Activity card, scoped to the
 * current workspace and the selected time range.
 *
 * Root query — reads `data.homeActivitySummary` directly (no unwrap).
 * Guards `currentWorkspace?.id` before issuing (undefined on first render).
 *
 * `rangeToDates()` is imported from `useHomeActivityMap` — do NOT re-implement.
 * Stale time is 60s (same as the map hook) so both stay in sync after refetch.
 */
export function useHomeActivitySummary(range: TimeRange): {
  data: HomeActivitySummary | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id;
  const { startDate, endDate } = rangeToDates(range);

  const { data, isLoading, error } = useCustomQuery({
    cacheKey: ['homeActivitySummary', workspaceId, range],
    query: HOME_ACTIVITY_SUMMARY_QUERY,
    variables: {
      filter: {
        // Scope to the current (possibly impersonated) workspace; the query is
        // gated on workspaceId below so it is always present (GRIPP #2859).
        workspaceId,
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      },
    },
    options: {
      // Do not issue the query until the workspace id is known.
      enabled: !!workspaceId,
      staleTime: 60_000,
    },
  });

  const summary: HomeActivitySummary | undefined = data?.homeActivitySummary;

  return { data: summary, isLoading, error: error as Error | null };
}
